import React, { Component } from 'react'

import PageBackLink from 'business/direct_quote_request/page/components/PageBackLink'
import StarRating from 'tailwind/common/components/StarRating'
import sentImage from 'icons/sent.png'
import lightingImage from 'icons/lighting.png'

const FULLY_VISIBLE_RATIO = 0.99
const VISIBILITY_TOLERANCE_PX = 1

export default class BusinessSidebar extends Component {
  state = { needsScroll: false }

  componentDidMount() {
    this.attachListeners()
  }

  componentDidUpdate(prevProps) {
    if (!this.listenersAttached && this.scrollEl && this.footerEl) {
      this.attachListeners()
    }

    const contentChanged = (
      prevProps.canCall !== this.props.canCall
      || prevProps.canBook !== this.props.canBook
      || prevProps.displayPhone !== this.props.displayPhone
      || prevProps.hasFeedbackScore !== this.props.hasFeedbackScore
    )

    if (contentChanged) {
      this.scheduleVisibilityCheck()
    }
  }

  componentWillUnmount() {
    this.detachListeners()
  }

  setScrollRef = (el) => {
    this.scrollEl = el
  }

  setFooterRef = (el) => {
    this.footerEl = el
  }

  attachListeners = () => {
    const scrollEl = this.scrollEl
    const footerEl = this.footerEl

    if (!scrollEl || !footerEl || this.listenersAttached) {
      return
    }

    this.listenersAttached = true

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(
        this.handleIntersection,
        { root: scrollEl, threshold: [0, FULLY_VISIBLE_RATIO, 1] }
      )
      this.intersectionObserver.observe(footerEl)
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleVisibilityCheck)
      this.resizeObserver.observe(scrollEl)
      this.resizeObserver.observe(footerEl)
      Array.from(scrollEl.children).forEach((child) => this.resizeObserver.observe(child))
    } else {
      window.addEventListener('resize', this.scheduleVisibilityCheck, false)
    }

    scrollEl.addEventListener('scroll', this.scheduleVisibilityCheck, false)

    this.scheduleVisibilityCheck()
  }

  detachListeners = () => {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.scheduleVisibilityCheck, false)
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    window.removeEventListener('resize', this.scheduleVisibilityCheck, false)

    this.listenersAttached = false

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  handleIntersection = (entries) => {
    const entry = entries[0]
    if (!entry) return

    const scrollEl = this.scrollEl
    const clipped = entry.intersectionRatio < FULLY_VISIBLE_RATIO
    const needsScroll = clipped || (scrollEl && scrollEl.scrollTop > 0)

    this.setNeedsScroll(needsScroll)
  }

  scheduleVisibilityCheck = () => {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
    }

    this.rafId = requestAnimationFrame(this.checkVisibility)
  }

  checkVisibility = () => {
    this.rafId = null

    const scrollEl = this.scrollEl
    const footerEl = this.footerEl

    if (!scrollEl || !footerEl) {
      return
    }

    const containerRect = scrollEl.getBoundingClientRect()
    const footerRect = footerEl.getBoundingClientRect()
    const tolerance = VISIBILITY_TOLERANCE_PX
    const fullyVisible = (
      footerRect.top >= containerRect.top - tolerance
      && footerRect.bottom <= containerRect.bottom + tolerance
    )

    this.setNeedsScroll(!fullyVisible || scrollEl.scrollTop > 0)
  }

  setNeedsScroll = (needsScroll) => {
    if (needsScroll !== this.state.needsScroll) {
      this.setState({ needsScroll })
    }
  }

  render() {
    const {
      businessName,
      suburbName,
      rating,
      reviews,
      hasFeedbackScore,
      hasLogo,
      logoUrl,
      logoInitials,
      logoColor,
      displayPhone,
      bookingPath,
      canCall,
      canBook,
      userName,
      respondsLabel,
      backToUrl,
      backToLabel,
    } = this.props

    const { needsScroll } = this.state

    return (
      <aside className="relative isolate w-80 shrink-0 self-stretch h-full min-h-0 max-h-full px-4 py-12 lg:w-96 lg:p-12 bg-base-blue-main flex flex-col overflow-hidden md:rounded-l-20">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64" aria-hidden="true">
          <div className="w-96 h-[454px] absolute opacity-60 bg-base-blue-950 blur-[123.60px] -right-20 bottom-[-120px]" />
          <div className="w-[504px] h-[638px] absolute opacity-75 bg-base-green-200 blur-[123.60px] left-[-200px] bottom-[-305px]" />
        </div>

        <div
          ref={this.setScrollRef}
          className={`relative flex-1 min-h-0 w-full overflow-x-hidden pr-2 lg:pr-3 ${needsScroll ? 'overflow-y-auto' : 'overflow-y-hidden'}`}
        >
          <div className="flex min-h-full w-full flex-col items-center gap-6">
            {backToUrl && backToLabel && (
              <div className="relative self-stretch shrink-0 flex items-center gap-2 w-full">
                <PageBackLink url={backToUrl} label={backToLabel} variant="sidebar" />
              </div>
            )}

            {/* Header */}
            <div className="relative self-stretch pb-2 inline-flex flex-col justify-start items-center">
              <div className="justify-start text-surface text-3xl font-extrabold">Request Quote</div>
              <div className="self-stretch text-center justify-start text-surface text-base font-normal">Send your job details and schedule. Get a response with a quote or next steps from the provider below.</div>
            </div>

            {/* Business info card */}
            <div className="relative w-full px-4 py-8 rounded-10 flex flex-col items-center gap-2">
              <div className="absolute inset-0 bg-white/20 rounded-10" />

              {/* Avatar */}
              <div className="relative w-[79px] h-[79px]">
                {hasLogo ? (
                  <img src={logoUrl} alt="Business logo" className="w-20 h-20 rounded-full object-cover shadow-md absolute top-0 left-0" />
                ) : (
                  <span
                    className="flex h-full w-full items-center justify-center text-lg font-semibold text-white rounded-full shadow-md"
                    style={{ backgroundColor: logoColor || '#000' }}>
                    {logoInitials}
                  </span>
                )}
                <div className="absolute w-10 h-10 -right-1.5 -bottom-1 bg-white rounded-full flex items-center justify-center overflow-hidden">
                  <img src={sentImage} className="w-[22px] h-[22px]" />
                </div>
              </div>

              {/* Info */}
              <div className="relative w-full min-w-0 flex flex-col justify-center items-center text-center gap-0.5">
                <div className="flex justify-start items-center gap-0.5">
                  <span className="justify-start text-surface text-lg font-extrabold">{userName}</span>
                  <hero-icon-solid name="check-circle" class="w-[13px] h-[13px] text-base-green-main"></hero-icon-solid>
                </div>
                <span className="max-w-full justify-start text-surface text-xl font-extrabold drop-shadow-xs wrap-anywhere">{businessName}</span>
                <span className="justify-start text-surface text-sm font-medium">{suburbName}</span>
                {hasFeedbackScore && (
                  <div className="flex items-center gap-0.5">
                    <span className="justify-start text-surface text-sm font-extrabold">{rating}</span>
                    <div className="flex items-center"><StarRating fontOptions='text-sm' rating={rating} starsOnly={true} /></div>
                    <span className="justify-start text-surface text-sm font-medium">
                       {reviews} {reviews === 1 ? 'Review' : 'Reviews'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer actions — visibility sentinel for scroll */}
            <div
              ref={this.setFooterRef}
              className="relative w-full mt-auto shrink-0 flex flex-col items-center gap-3"
            >
              <div className="flex items-center gap-2">
                <img src={lightingImage} className="w-6 h-6 aspect-square" />
                <span className="justify-center text-surface text-base font-medium">{respondsLabel}</span>
              </div>
              {displayPhone && (
                <p className="justify-start text-surface text-3xl font-black">
                  {displayPhone}
                </p>
              )}
              {canCall && displayPhone && (
                <a href={`tel:${displayPhone}`} className="btn-neutral btn-base-large btn-inline-block">
                  Call
                </a>
              )}
              {canBook && (
                <a href={bookingPath} className="btn-green-main btn-base-large btn-inline-block">
                  Book
                </a>
              )}
            </div>
          </div>
        </div>
      </aside>
    )
  }
}
